import React from 'react';
import { Search, Megaphone, DollarSign, Store, LineChart, ArrowRight } from 'lucide-react';

const services = [
  {
    icon: Search,
    title: 'App Growth Audit',
    body: 'A focused review of your current app funnel, paid channels, tracking setup, store presence, and revenue opportunities.',
    bestFor: 'Teams that want an outside view before committing to a larger project.',
    deliverables: [
      'Growth bottleneck review',
      'Acquisition channel assessment',
      'App-store conversion observations',
      'Tracking and measurement gaps',
      'Prioritized 30-day action plan',
    ],
    cta: 'Request an App Growth Audit',
  },
  {
    icon: Megaphone,
    title: 'Performance Marketing for Apps',
    body: 'Planning, setup, optimization, and reporting for paid acquisition campaigns across relevant channels.',
    bestFor: 'Apps actively investing in paid growth.',
    deliverables: [
      'Meta Ads',
      'Google App Campaigns',
      'Apple Search Ads',
      'TikTok Ads',
      'Creative testing',
      'Budget allocation',
      'Funnel reporting',
    ],
    cta: 'Explore performance marketing',
  },
  {
    icon: DollarSign,
    title: 'Subscription & Revenue Growth',
    body: 'For apps with subscriptions, in-app purchases, paid content, memberships, or transactions.',
    bestFor: 'Apps that need more paying customers, not just downloads.',
    deliverables: [
      'Trial conversion analysis',
      'Pricing / package testing',
      'Onboarding improvements',
      'Lifecycle & retention campaigns',
      'Churn & reactivation opportunities',
    ],
    cta: 'Improve subscription revenue',
  },
  {
    icon: Store,
    title: 'App Store & Creative Optimization',
    body: 'Improve the conversion from ad click or organic visit to install.',
    bestFor: 'Apps with strong traffic but weak store conversion.',
    deliverables: [
      'App Store / Google Play page review',
      'Screenshot and message testing',
      'Ad creative concepts',
      'Audience-message fit',
      'Competitor positioning review',
    ],
    cta: 'Boost app-store conversion',
  },
  {
    icon: LineChart,
    title: 'Tracking, Analytics & Growth Reporting',
    body: 'Make performance easier to measure and act on.',
    bestFor: 'Teams struggling to trust their numbers or attribution.',
    deliverables: [
      'Event tracking plan',
      'GA4 / Firebase setup',
      'AppsFlyer / Adjust collaboration',
      'SKAN measurement logic',
      'Executive growth dashboard',
    ],
    cta: 'Fix your growth reporting',
  },
];

const Services = () => {
  const scrollToForm = () =>
    document.getElementById('app-growth-lead-form')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      data-testid="app-growth-services"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <div className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold uppercase tracking-wider mb-4">
            What we do
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
            App growth services.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                className="group bg-white border border-gray-100 rounded-2xl p-7 hover:border-blue-200 hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-5">
                  <Icon size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{s.body}</p>
                <div className="text-xs uppercase tracking-wide text-blue-600 font-semibold mb-2">
                  Best for
                </div>
                <p className="text-sm text-gray-700 italic mb-4">{s.bestFor}</p>
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                  Includes
                </div>
                <ul className="space-y-1.5 mb-6 flex-1">
                  {s.deliverables.map((d, j) => (
                    <li key={j} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0 mt-2" />
                      {d}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToForm}
                  className="mt-auto text-sm font-semibold text-blue-700 hover:text-blue-600 inline-flex items-center gap-1 group/cta"
                >
                  {s.cta}
                  <ArrowRight size={14} className="group-hover/cta:translate-x-1 transition-transform" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
